import { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { trackEvent } from '@/lib/analytics';

interface FavoritesContextType {
  favorites: Set<string>;
  toggleFavorite: (productId: string) => Promise<void>;
  isFavorite: (productId: string) => boolean;
  isLoading: boolean;
  clearAllFavorites: () => void;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

const FAVORITES_STORAGE_KEY = 'gettested_favorites';
const SESSION_ID_KEY = 'user_session_id';

const getSessionId = (): string => {
  let sessionId = localStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  return sessionId;
};

export const FavoritesProvider = ({ children }: { children: ReactNode }) => {
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const userIdRef = useRef<string | undefined>(undefined);

  // Load favorites from localStorage on mount
  useEffect(() => {
    const loadFavorites = () => {
      try {
        const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
        console.log('[Favorites] Raw localStorage value:', stored);
        if (stored) {
          const favoriteIds = JSON.parse(stored);
          console.log('[Favorites] Parsed IDs count:', favoriteIds?.length, 'IDs:', favoriteIds);
          
          // Defensive check: if more than 100 favorites, likely corrupted data
          if (Array.isArray(favoriteIds) && favoriteIds.length > 100) {
            console.warn('[Favorites] Unusually large favorites count detected, clearing corrupted data');
            localStorage.removeItem(FAVORITES_STORAGE_KEY);
            setFavorites(new Set());
          } else if (Array.isArray(favoriteIds)) {
            setFavorites(new Set(favoriteIds));
          } else {
            console.warn('[Favorites] Invalid favorites data format, clearing');
            localStorage.removeItem(FAVORITES_STORAGE_KEY);
            setFavorites(new Set());
          }
        }
      } catch (error) {
        console.error('Error loading favorites from localStorage:', error);
        localStorage.removeItem(FAVORITES_STORAGE_KEY);
        setFavorites(new Set());
      }
    };

    loadFavorites();
    setIsLoading(false);
  }, []);

  // Check auth state and sync with database if user is logged in
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      userIdRef.current = session?.user?.id;
      if (session?.user) {
        syncWithDatabase(session.user.id);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      userIdRef.current = session?.user?.id;
      if (session?.user) {
        syncWithDatabase(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Save favorites to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(Array.from(favorites)));
    } catch (error) {
      console.error('Error saving favorites to localStorage:', error);
    }
  }, [favorites]);

  const syncWithDatabase = async (userId: string) => {
    try {
      // Get favorites from database
      const { data: dbFavorites, error } = await supabase
        .from('favorites')
        .select('product_id')
        .eq('user_id', userId);

      if (error) throw error;

      const dbFavoriteIds = new Set(dbFavorites?.map(f => f.product_id) || []);
      const localFavoriteIds = new Set(favorites);

      // Merge local and database favorites
      const mergedFavorites = new Set([...dbFavoriteIds, ...localFavoriteIds]);

      // Update database with any local favorites that aren't in the database
      const favoritesToAdd = Array.from(localFavoriteIds).filter(id => !dbFavoriteIds.has(id));
      
      if (favoritesToAdd.length > 0) {
        const { error: insertError } = await supabase
          .from('favorites')
          .insert(favoritesToAdd.map(productId => ({ user_id: userId, product_id: productId })));
        
        if (insertError) throw insertError;
      }

      setFavorites(mergedFavorites);
    } catch (error) {
      console.error('Error syncing favorites with database:', error);
    }
  };

  const toggleFavorite = async (productId: string) => {
    const isFav = favorites.has(productId);

    // Update local state immediately
    setFavorites(prev => {
      const newSet = new Set(prev);
      if (isFav) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });

    // Track favorite event
    trackEvent({
      type: isFav ? 'favorite_removed' : 'favorite_added',
      productId,
      category: 'engagement',
      sessionId: getSessionId(),
      userId: userIdRef.current,
    });

    // If user is logged in, sync with database
    if (user) {
      try {
        if (isFav) {
          const { error } = await supabase
            .from('favorites')
            .delete()
            .eq('user_id', user.id)
            .eq('product_id', productId);

          if (error) throw error;
        } else {
          const { error } = await supabase
            .from('favorites')
            .insert({ user_id: user.id, product_id: productId });

          if (error) throw error;
        }
      } catch (error) {
        console.error('Error syncing favorite with database:', error);
        // Revert local state if database sync fails
        setFavorites(prev => {
          const newSet = new Set(prev);
          if (isFav) {
            newSet.add(productId);
          } else {
            newSet.delete(productId);
          }
          return newSet;
        });
      }
    }
  };

  const isFavorite = (productId: string) => favorites.has(productId);

  const clearAllFavorites = () => {
    console.log('[Favorites] Clearing all favorites');
    setFavorites(new Set());
    localStorage.removeItem(FAVORITES_STORAGE_KEY);
  };

  return (
    <FavoritesContext.Provider value={{ favorites, toggleFavorite, isFavorite, isLoading, clearAllFavorites }}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};
